import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  useCreateTripExit,
  useEditingTripId,
  useTripFormMode,
  useTripFormOnSaved,
  useTripFormVariant,
} from '../create-trip-context'
import { Box, Stack } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'

import ActionButtons from 'components/action-buttons/action-buttons.component'
import ConfirmationDialog from 'components/confirmation-dialog/confirmation-dialog.component'
import AceTripDialog from '../ace-trip/ace-trip-dialog'
import {
  BorderConnectPrefill,
  buildBorderConnectPrefill,
  buildCrossBorderShipmentPairs,
  getPickupAndDeliveryTasks,
  resolveNumericTripId,
  resolvePreferredManifestKind,
} from '../ace-trip/ace-trip-autofill'
import { TripPageType, TripPayload } from 'store/trips/trips.types'
import {
  addDrivers,
  clearTripPayload,
  setTripCreateCompletion,
} from 'store/trips/trips.slice'
import { DriverFixture } from 'store/trips/trips.fixtures'
import { prepareTripTasks } from 'store/trips/trips.utils'
import {
  useCreateTripMutation,
  useDeleteTripMutation,
  useEditTripMutation,
} from 'store/trips/trips.api'
import {
  useLazyGetOrderByIdQuery,
  useLazyGetCarrierLoadConfirmationsQuery,
  useUpdateOrderStatusMutation,
} from 'store/orders/orders.api'
import { ordersApi } from 'store/orders/orders.api'
import LoadConfirmationDialog from 'pages/carrier-trip/load-confirmation/load-confirmation-dialog'
import CarrierEmailDialog from 'pages/carrier-trip/load-confirmation/carrier-email-dialog'
import {
  CarrierLcSaveResult,
} from 'pages/carrier-trip/load-confirmation/use-send-to-carrier'
import {
  resolveSourceOrderId,
  defaultLoadConfirmationFormValues,
  loadConfirmationFormSchema,
  LoadConfirmationFormValues,
  CarrierContactDetails,
  fetchCarrierContactDetails,
} from 'modules/load-confirmation'
import { Order } from 'store/orders/orders.types'
import { useLazyListInvoicedocumentsQuery } from 'store/invoices/invoices.api'
import { getLatestLoadConfirmationS3Path } from 'utils/load-confirmation-documents'
import { getUserId } from 'utils/common'
import { useLazyGetIntegrationsQuery } from 'store/integrations/integrations.api'
import { useLazyGeocodeAddressQuery } from 'store/address/address.api'
import { useLazyListDriversQuery } from 'store/drivers/drivers.api'
import { useLazyListEquipmentsQuery } from 'store/equipments/equipments.api'

import TripDetailsTable from './trip-details-table/trip-details-table'

type TripTableProps = {
  tripTasks?: TripPayload[]
}

const TripTable = ({ tripTasks }: TripTableProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const onExit = useCreateTripExit()
  const mode = useTripFormMode()
  const variant = useTripFormVariant()
  const editingTripId = useEditingTripId()
  const onSaved = useTripFormOnSaved()
  const isEditMode = mode === 'edit'
  const isCarrierVariant = variant === 'carrier'

  const [loadConfirmationOpen, setLoadConfirmationOpen] = useState(
    () => isCarrierVariant
  )
  const [carrierEmailOpen, setCarrierEmailOpen] = useState(false)
  const [carrierLcContext, setCarrierLcContext] =
    useState<CarrierLcSaveResult | null>(null)
  const pendingEmailAfterSaveRef = useRef(false)
  const [existingCarrierOrder, setExistingCarrierOrder] = useState<Order | null>(null)
  const [prefilledCarrierContact, setPrefilledCarrierContact] =
    useState<CarrierContactDetails | null>(null)
  const [borderConnectConfirmOpen, setBorderConnectConfirmOpen] =
    useState(false)
  const [aceTripFormOpen, setAceTripFormOpen] = useState(false)
  const [borderConnectPrefill, setBorderConnectPrefill] =
    useState<BorderConnectPrefill | null>(null)
  const [isSending, setIsSending] = useState(false)
  const sendingRef = useRef(false)
  const pendingDriverNameRef = useRef('')

  const loadConfirmationForm = useForm<LoadConfirmationFormValues>({
    resolver: yupResolver(loadConfirmationFormSchema),
    defaultValues: defaultLoadConfirmationFormValues,
  })

  const exitCreateTrip = () => {
    if (onExit) {
      onExit()
      return
    }
    navigate('..')
  }

  const [createTrip, { isError: isCreateTripError }] = useCreateTripMutation()
  const [editTrip] = useEditTripMutation()
  const [deleteTrip] = useDeleteTripMutation()
  const [listCarrierLoadConfirmations] = useLazyGetCarrierLoadConfirmationsQuery()
  const [getOrderById] = useLazyGetOrderByIdQuery()
  const [fetchOrderDocuments] = useLazyListInvoicedocumentsQuery()
  const [updateOrderStatus, { isError: isUpdateStatusError }] =
    useUpdateOrderStatusMutation()
  const [getIntegrations] = useLazyGetIntegrationsQuery()
  const [geocodeAddress] = useLazyGeocodeAddressQuery()
  const [listDrivers] = useLazyListDriversQuery()
  const [listEquipments] = useLazyListEquipmentsQuery()

  const firstDriver = useSelector((state: RootState) => state.trips.firstDriver)
  const tripPayload = useSelector(
    (state: RootState) => state.trips.tripsPayload
  )
  const orderId = useSelector((state: RootState) => state.trips.orderId)
  const { newOrders, activerOrders } = useSelector(
    (state: RootState) => state.orders
  )

  const allOrders = [...(newOrders ?? []), ...(activerOrders ?? [])]
  const hasTripTasks = !!tripTasks && tripTasks.length > 0
  const hasOrderEvents =
    tripTasks?.some(
      (task) => task.type === 'PICKUP' || task.type === 'DELIVERY'
    ) ?? false
  const hasDriver = Boolean(
    firstDriver.id?.trim() || tripPayload.firstDriverId?.trim()
  )
  const showSaveButton = isEditMode || hasTripTasks
  const saveRequiresDriver = isEditMode ? hasOrderEvents : true
  const sourceOrderId = resolveSourceOrderId(tripPayload.tripTasks ?? [], allOrders)
  const tripCarrier = useMemo(
    () => ({
      carrierName: tripPayload.firstDriverName,
      carrierCompanyId: tripPayload.firstDriverId,
    }),
    [tripPayload.firstDriverName, tripPayload.firstDriverId]
  )

  const handleCancel = () => {
    exitCreateTrip()
  }

  const handleLoadConfirmationClose = () => {
    setLoadConfirmationOpen(false)
    // Carrier flow is LC + email only. Closing LC without a save exits.
    if (isCarrierVariant && !pendingEmailAfterSaveRef.current) {
      exitCreateTrip()
    }
    pendingEmailAfterSaveRef.current = false
  }

  const handleCarrierEmailClose = () => {
    setCarrierEmailOpen(false)
    if (isCarrierVariant) {
      exitCreateTrip()
    }
  }

  useEffect(() => {
    if (isCarrierVariant) {
      setLoadConfirmationOpen(true)
    }
  }, [isCarrierVariant])

  const hydrateCarrierLcState = useCallback(
    async (
      carrierOrderId: number,
      options: {
        carrierCompanyId?: string
        carrierName?: string
        loadNumber?: string
        sourceOrderId?: number
        dispatchEmail?: string
        s3Path?: string
      }
    ) => {
      const carrierCompanyId = options.carrierCompanyId?.trim() ?? ''

      const [carrierOrder, docs, carrierContact] = await Promise.all([
        getOrderById({ orderId: carrierOrderId }).unwrap(),
        fetchOrderDocuments({ orderId: String(carrierOrderId) }).unwrap(),
        carrierCompanyId
          ? fetchCarrierContactDetails(dispatch, carrierCompanyId)
          : Promise.resolve(null),
      ])

      setExistingCarrierOrder(carrierOrder)
      setPrefilledCarrierContact(carrierContact)

      const latestLcS3Path = getLatestLoadConfirmationS3Path(docs.LC)
      const resolvedSourceOrderId =
        options.sourceOrderId ?? sourceOrderId ?? carrierOrder.orderId

      setCarrierLcContext({
        carrierOrderId: carrierOrder.orderId,
        carrierCompanyId: carrierCompanyId || carrierOrder.companyId,
        carrierName: options.carrierName?.trim() || carrierOrder.customerName,
        dispatchEmail:
          options.dispatchEmail ?? carrierOrder.notificationEmail ?? '',
        loadNumber:
          options.loadNumber ??
          (resolvedSourceOrderId ? `LCLC-${resolvedSourceOrderId}` : ''),
        s3Path: latestLcS3Path || options.s3Path || '',
        sourceOrderId: resolvedSourceOrderId ?? carrierOrder.orderId,
      })
    },
    [dispatch, fetchOrderDocuments, getOrderById, sourceOrderId]
  )

  const handleLoadConfirmationSaved = async (result?: unknown) => {
    const saveResult = result as CarrierLcSaveResult | undefined
    if (!saveResult) return

    // Keep create/edit open so the email dialog can show after LC closes.
    pendingEmailAfterSaveRef.current = true
    setCarrierLcContext(saveResult)

    dispatch(
      addDrivers([
        {
          ...DriverFixture,
          id: saveResult.carrierCompanyId,
          firstName: saveResult.carrierName,
          lastName: '',
        },
        { ...DriverFixture, id: '', firstName: '', lastName: '' },
      ])
    )

    try {
      await hydrateCarrierLcState(saveResult.carrierOrderId, {
        carrierCompanyId: saveResult.carrierCompanyId,
        carrierName: saveResult.carrierName,
        loadNumber: saveResult.loadNumber,
        sourceOrderId: saveResult.sourceOrderId,
        dispatchEmail: saveResult.dispatchEmail,
        s3Path: saveResult.s3Path,
      })
    } catch (error) {
      console.error('Error loading saved load confirmation', error)
      setCarrierLcContext(saveResult)
    }

    dispatch(ordersApi.util.invalidateTags(['Orders']))
    setCarrierEmailOpen(true)
  }

  const handleCarrierEmailSent = async () => {
    if (!carrierLcContext) return

    const syncedTripPayload = {
      ...tripPayload,
      firstDriverId: carrierLcContext.carrierCompanyId,
      firstDriverName: carrierLcContext.carrierName,
      secondDriverId: '',
      secondDriverName: '',
      tripType: 'CARRIER_TRIP' as const,
      tripTasks: prepareTripTasks(tripPayload.tripTasks),
    }

    try {
      if (isEditMode && editingTripId) {
        await editTrip({
          ...syncedTripPayload,
          tripId: editingTripId,
        }).unwrap()
      } else {
        await createTrip(syncedTripPayload).unwrap()
        await updateOrderStatus({
          orderId: carrierLcContext.sourceOrderId,
          status: 'ACTIVE',
        }).unwrap()
      }

      if (!isCreateTripError && !isUpdateStatusError) {
        dispatch(clearTripPayload())
        dispatch(ordersApi.util.invalidateTags(['Orders']))
        onSaved?.()
        exitCreateTrip()
      }
    } catch (error) {
      console.error('Error saving carrier trip after email send', error)
    }
  }

  const completeDriverTripCreate = () => {
    setBorderConnectPrefill(null)
    dispatch(clearTripPayload())
    dispatch(
      setTripCreateCompletion({
        isComplete: true,
        driverName: pendingDriverNameRef.current,
      })
    )
    exitCreateTrip()
  }

  const activateLinkedOrder = async () => {
    const orderIdToActivate = sourceOrderId ?? (orderId > 0 ? orderId : null)
    if (!orderIdToActivate) return
    try {
      await updateOrderStatus({
        orderId: orderIdToActivate,
        status: 'ACTIVE',
      }).unwrap()
    } catch (statusError) {
      console.error('Error updating order status after trip create', statusError)
    }
  }

  const handleBorderConnectNo = () => {
    setBorderConnectConfirmOpen(false)
    completeDriverTripCreate()
  }

  const handleBorderConnectYes = () => {
    setBorderConnectConfirmOpen(false)
    setAceTripFormOpen(true)
  }

  const handleAceTripClose = () => {
    setAceTripFormOpen(false)
    completeDriverTripCreate()
  }

  const handleSend = async () => {
    if (sendingRef.current) return
    sendingRef.current = true
    setIsSending(true)

    const tripType: TripPageType = isCarrierVariant ? 'CARRIER_TRIP' : 'DRIVER_TRIP'
    const syncedTripPayload = {
      ...tripPayload,
      ...(isCarrierVariant && carrierLcContext
        ? {
            firstDriverId: carrierLcContext.carrierCompanyId,
            firstDriverName: carrierLcContext.carrierName,
            secondDriverId: '',
            secondDriverName: '',
          }
        : {}),
      tripType,
      tripTasks: prepareTripTasks(tripPayload.tripTasks),
    }

    if (isEditMode && editingTripId) {
      try {
        const payloadHasDriver = Boolean(
          syncedTripPayload.firstDriverId?.trim()
        )
        const payloadHasOrderEvents =
          syncedTripPayload.tripTasks?.some(
            (task) => task.type === 'PICKUP' || task.type === 'DELIVERY'
          ) ?? false

        if (payloadHasOrderEvents && !payloadHasDriver) {
          return
        }

        const shouldRemoveTrip =
          !syncedTripPayload.tripTasks?.length ||
          (!payloadHasDriver && !payloadHasOrderEvents)

        if (shouldRemoveTrip) {
          await deleteTrip({ tripId: editingTripId.toString() }).unwrap()
        } else {
          await editTrip({
            ...syncedTripPayload,
            tripId: editingTripId,
          }).unwrap()
        }

        dispatch(ordersApi.util.invalidateTags(['Orders']))
        dispatch(clearTripPayload())
        onSaved?.()
        if (!onExit) {
          exitCreateTrip()
        }
      } catch (error) {
        console.error('Error saving trip', error)
      } finally {
        sendingRef.current = false
        setIsSending(false)
      }
      return
    }

    try {
      const created = await createTrip(syncedTripPayload).unwrap()
      pendingDriverNameRef.current = syncedTripPayload?.firstDriverName ?? ''
      await activateLinkedOrder()

      try {
        const userId = getUserId()
        const integrations = userId
          ? await getIntegrations({ userId }).unwrap()
          : []
        const borderConnect = integrations.find(
          (item) => item.integrationType === 'BORDERCONNECT' && item.enabled
        )

        const tasks = syncedTripPayload.tripTasks ?? []
        const stopTasks = getPickupAndDeliveryTasks(tasks)
        const hasPickup = stopTasks.some((task) => task.type === 'PICKUP' && task.address)
        const hasDelivery = stopTasks.some(
          (task) => task.type === 'DELIVERY' && task.address
        )

        if (!borderConnect || !hasPickup || !hasDelivery) {
          completeDriverTripCreate()
          return
        }

        const uniqueAddresses = Array.from(
          new Set(
            stopTasks
              .map((task) => task.address)
              .filter((address): address is string => !!address?.trim())
          )
        )
        const orderIdsToFetch = Array.from(
          new Set(
            stopTasks
              .map((task) => task.sourceOrderPkId)
              .filter((orderId): orderId is number => orderId != null)
          )
        )

        const [drivers, equipments, fetchedOrders, ...geoResults] =
          await Promise.all([
            listDrivers({ userId }).unwrap().catch(() => []),
            listEquipments().unwrap().catch(() => []),
            Promise.all(
              orderIdsToFetch.map((orderId) =>
                getOrderById({ orderId }).unwrap().catch(() => undefined)
              )
            ),
            ...uniqueAddresses.map((address) =>
              geocodeAddress({ address }).unwrap().catch(() => undefined)
            ),
          ])

        const geoByAddress: Record<string, (typeof geoResults)[number]> = {}
        uniqueAddresses.forEach((address, index) => {
          geoByAddress[address] = geoResults[index]
        })

        const pairs = buildCrossBorderShipmentPairs(tasks, geoByAddress)
        const manifestKind = resolvePreferredManifestKind(pairs)

        if (!manifestKind) {
          completeDriverTripCreate()
          return
        }

        const driver =
          drivers.find((item) => item.id === syncedTripPayload.firstDriverId) ??
          firstDriver
        const orders = [
          ...fetchedOrders.filter(
            (order): order is NonNullable<typeof order> => order != null
          ),
          ...allOrders,
        ]

        setBorderConnectPrefill(
          buildBorderConnectPrefill({
            manifestKind,
            scacCode: borderConnect.scacCode,
            carrierCode: borderConnect.carrierCode,
            numericTripId: resolveNumericTripId(created),
            tripTasks: tasks,
            driver,
            equipments,
            geoByAddress,
            orders,
            pairs,
          })
        )
        setBorderConnectConfirmOpen(true)
      } catch (prepareError) {
        console.error('Error preparing Border Connect prompt', prepareError)
        completeDriverTripCreate()
      }
    } catch (error) {
      console.error('Error creating trip', error)
    } finally {
      sendingRef.current = false
      setIsSending(false)
    }
  }

  useEffect(() => {
    if (!isCarrierVariant) {
      dispatch(addDrivers([firstDriver, DriverFixture]))
      return
    }

    if (!isEditMode) {
      dispatch(
        addDrivers([
          { ...DriverFixture, id: '', firstName: '', lastName: '' },
          DriverFixture,
        ])
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstDriver, isCarrierVariant, isEditMode])

  useEffect(() => {
    if (!isCarrierVariant || !isEditMode || !sourceOrderId) {
      return
    }

    let active = true
    const loadNumber = `LCLC-${sourceOrderId}`
    const tripCarrierId = tripPayload.firstDriverId?.trim() ?? ''
    const tripCarrierName = tripPayload.firstDriverName?.trim() ?? ''

    const hydrateExistingCarrierLoadConfirmation = async () => {
      try {
        const carrierOrders = await listCarrierLoadConfirmations({
          sourceOrderId,
        }).unwrap()

        const latestCarrierOrder =
          (tripCarrierId
            ? carrierOrders.find((order) => order.companyId?.trim() === tripCarrierId)
            : undefined) ??
          (tripCarrierName
            ? carrierOrders.find(
                (order) =>
                  order.customerName?.trim().toLowerCase() ===
                  tripCarrierName.toLowerCase()
              )
            : undefined) ??
          carrierOrders[0] ??
          null

        if (!latestCarrierOrder?.orderId) {
          if (!active) return
          setExistingCarrierOrder(null)

          if (tripCarrierId) {
            const carrierContact = await fetchCarrierContactDetails(
              dispatch,
              tripCarrierId
            )
            if (!active) return
            setPrefilledCarrierContact(carrierContact)
          } else {
            setPrefilledCarrierContact(null)
          }
          return
        }

        if (!active) return
        await hydrateCarrierLcState(latestCarrierOrder.orderId, {
          carrierCompanyId:
            tripCarrierId || latestCarrierOrder.companyId?.trim() || '',
          carrierName: tripCarrierName || latestCarrierOrder.customerName,
          loadNumber,
          sourceOrderId,
        })
      } catch (error) {
        console.error('Error loading existing load confirmation', error)
      }
    }

    hydrateExistingCarrierLoadConfirmation()

    return () => {
      active = false
    }
  }, [
    dispatch,
    hydrateCarrierLcState,
    isCarrierVariant,
    isEditMode,
    listCarrierLoadConfirmations,
    sourceOrderId,
    tripPayload.firstDriverId,
    tripPayload.firstDriverName,
  ])

  if (isCarrierVariant) {
    return (
      <>
        <LoadConfirmationDialog
          open={loadConfirmationOpen}
          onClose={handleLoadConfirmationClose}
          formMethods={loadConfirmationForm}
          tripTasks={tripPayload.tripTasks ?? []}
          allOrders={allOrders}
          existingCarrierOrder={existingCarrierOrder}
          tripCarrier={tripCarrier}
          prefilledCarrierContact={prefilledCarrierContact}
          skipCarrierAutoFill={
            !!existingCarrierOrder || !!prefilledCarrierContact
          }
          onSaved={handleLoadConfirmationSaved}
        />

        {carrierLcContext && (
          <CarrierEmailDialog
            open={carrierEmailOpen}
            onClose={handleCarrierEmailClose}
            carrierContext={carrierLcContext}
            onSent={handleCarrierEmailSent}
          />
        )}
      </>
    )
  }

  return (
    <Stack
      sx={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          overscrollBehaviorY: 'contain',
        }}
      >
        <TripDetailsTable tripTasks={tripTasks} />
      </Box>

      <ActionButtons
        containerSx={{ flexShrink: 0 }}
        firstBtnProps={{
          title: 'Cancel',
          onClick: handleCancel,
          bgcolor: 'secondary.main',
        }}
        {...(showSaveButton
          ? {
              secondBtnProps: {
                title: isEditMode ? 'Save Trip' : 'Send to Driver',
                onClick: handleSend,
                bgcolor: 'primary.main',
                disabled:
                  (saveRequiresDriver && !hasDriver) ||
                  isSending ||
                  borderConnectConfirmOpen ||
                  aceTripFormOpen,
              },
            }
          : {})}
      />

      <ConfirmationDialog
        title="Border Connect"
        open={borderConnectConfirmOpen}
        maxWidth="sm"
        fullWidth
        handleClose={handleBorderConnectNo}
        cancelBtnText="No"
        cancelBtnTextStyle={{
          sx: {
            bgcolor: 'secondary.main',
            color: 'common.white',
            '&:hover': { bgcolor: 'grey.600' },
          },
        }}
        successBtnText="Yes"
        handleSuccessBtn={handleBorderConnectYes}
        handleCancelBtn={handleBorderConnectNo}
      >
        Do you want to send this trip to Border Connect?
      </ConfirmationDialog>

      <AceTripDialog
        open={aceTripFormOpen}
        onClose={handleAceTripClose}
        prefill={borderConnectPrefill}
      />
    </Stack>
  )
}

const TripDetails = () => {
  const tripPayload = useSelector(
    (state: RootState) => state.trips.tripsPayload
  )

  return <TripTable tripTasks={tripPayload?.tripTasks} />
}

export default TripDetails

